import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import {
  EmptyTrashBatchResponse,
  MessageActionResponse,
  MessageBody,
  RemoteImageSenderPreference,
  SearchMessageActionResponse,
  SearchResponse,
  ThreadListResponse,
  ThreadOpenResponse,
  ThreadSnoozeResponse,
  type MessageActionRequest,
  type MailSearchSort,
  type MessageActionUndo,
  type RemoteImagePolicy,
  type SearchMessageActionRequest,
  type ThreadDetail as ThreadDetailType,
  type ThreadListResponse as ThreadListResponseType,
  type ThreadOpenResponse as ThreadOpenResponseType,
  type ThreadRow,
  type ThreadSnoozeRequest,
} from '@email/shared';
import { api } from './client.js';
import {
  drainEmptyTrashBatches,
  type EmptyTrashProgress,
  type EmptyTrashResult,
} from './empty-trash.js';
import {
  mailInvalidations,
  mailKeys,
  type MailScope,
  type RemoteImageRequestMode,
} from './query-cache.js';

export type { EmptyTrashProgress, EmptyTrashResult } from './empty-trash.js';
export {
  mailKeys,
  mailScopeKey as scopeKey,
  type MailScope,
  type RemoteImageRequestMode,
} from './query-cache.js';

function listParams(scope: MailScope, bucket: string, cursor?: string): string {
  const params = new URLSearchParams();
  if (typeof scope === 'string') {
    params.set('scope', scope);
  } else {
    params.set('scope', String(scope.accountId));
    params.set('folderId', String(scope.folderId));
  }
  params.set('bucket', bucket);
  if (cursor) params.set('cursor', cursor);
  return params.toString();
}

export function useSnoozeThreads() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ThreadSnoozeRequest) =>
      api('/api/threads/snooze', {
        method: 'POST',
        body,
        schema: ThreadSnoozeResponse,
      }),
    onSettled: () => mailInvalidations.actions.invalidate(qc),
  });
}

export function useUnsnoozeThreads() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (threadIds: number[]) =>
      api('/api/threads/unsnooze', {
        method: 'POST',
        body: { threadIds },
        schema: ThreadSnoozeResponse,
      }),
    onSettled: () => mailInvalidations.actions.invalidate(qc),
  });
}

/** Unread section: single page, capped — Gmail's unread-first top section. */
export function useUnreadList(scope: MailScope, enabled = true) {
  return useQuery({
    queryKey: mailKeys.list(scope, 'unread'),
    queryFn: () =>
      api(`/api/threads?${listParams(scope, 'unread')}&limit=100`, {
        schema: ThreadListResponse,
      }),
    enabled,
    // A dropped SSE event must not strand an empty view while initial
    // backfill is adding its first headers.
    refetchInterval: (query) =>
      query.state.data?.rows.length === 0 ? 30_000 : false,
  });
}

/** Everything-else (or flat all) section: infinite scroll. */
export function useThreadList(scope: MailScope, bucket: 'rest' | 'all', enabled = true) {
  return useInfiniteQuery({
    queryKey: mailKeys.list(scope, bucket),
    queryFn: ({ pageParam }) =>
      api(`/api/threads?${listParams(scope, bucket, pageParam || undefined)}`, {
        schema: ThreadListResponse,
      }),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    refetchInterval: (query) =>
      query.state.data?.pages.every((page) => page.rows.length === 0)
        ? 30_000
        : false,
  });
}

/** Full-history FTS results use the same cursor/page shape as normal mail lists. */
export function useSearchResults(
  query: string,
  sort: MailSearchSort = 'newest',
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: mailKeys.search(query, sort),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ q: query, sort });
      if (pageParam) params.set('cursor', pageParam);
      return api(`/api/search?${params.toString()}`, { schema: SearchResponse });
    },
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor,
    enabled,
  });
}

export function useThread(
  threadId: number,
  images: RemoteImageRequestMode = 'blocked',
) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: mailKeys.thread(threadId),
    queryFn: async ({ signal }) => {
      // Invalidations refetch metadata without retransmitting a potentially
      // large body that is already held under its dedicated immutable key.
      const includeInitialBody = qc.getQueryData(mailKeys.thread(threadId)) === undefined;
      const opened = await fetchThreadOpen(threadId, images, includeInitialBody, signal);
      primeInitialMessageBody(qc, opened, images);
      return opened;
    },
  });
}

export function fetchThreadOpen(
  threadId: number,
  images: RemoteImageRequestMode = 'blocked',
  includeInitialBody = true,
  signal?: AbortSignal,
) {
  const query = remoteImageQuery(images, includeInitialBody ? null : ['initialBody', '0']);
  return api(`/api/threads/${threadId}${query}`, { schema: ThreadOpenResponse, signal });
}

/** Begin an intentional row's body work before navigation completes. */
export async function prefetchThreadOpen(
  qc: QueryClient,
  threadId: number,
  images: RemoteImageRequestMode,
): Promise<void> {
  const threadKey = mailKeys.thread(threadId);
  const cached = qc.getQueryData<ThreadOpenResponseType>(threadKey);
  if (cached) {
    const visibleMessage =
      cached.messages.find((message) => !message.isRead) ?? cached.messages.at(-1);
    if (
      visibleMessage &&
      qc.getQueryData(mailKeys.body(visibleMessage.id, images)) === undefined
    ) {
      await qc.prefetchQuery({
        queryKey: mailKeys.body(visibleMessage.id, images),
        queryFn: ({ signal }) => fetchMessageBody(visibleMessage.id, images, signal),
        staleTime: 0,
      });
    }
    return;
  }

  await qc.prefetchQuery({
    queryKey: threadKey,
    queryFn: async ({ signal }) => {
      const opened = await fetchThreadOpen(threadId, images, true, signal);
      primeInitialMessageBody(qc, opened, images);
      return opened;
    },
    staleTime: 30_000,
  });
}

export function remoteImageRequestMode(policy: RemoteImagePolicy): RemoteImageRequestMode {
  return policy === 'proxy' ? 'standard' : policy;
}

export function primeInitialMessageBody(
  qc: QueryClient,
  opened: Pick<ThreadOpenResponseType, 'initialBody'>,
  images: RemoteImageRequestMode,
): void {
  if (!opened.initialBody) return;
  qc.setQueryData(
    mailKeys.body(opened.initialBody.messageId, images),
    opened.initialBody.body,
  );
}

export function useMessageBody(
  messageId: number,
  images: RemoteImageRequestMode,
  enabled: boolean,
) {
  return useQuery({
    queryKey: mailKeys.body(messageId, images),
    queryFn: ({ signal }) => fetchMessageBody(messageId, images, signal),
    enabled,
    // Completed message source is immutable for this local row. Pending state
    // is immediately stale so a body fetch started by the thread fast path is
    // checked again as soon as the card mounts.
    staleTime: (query) => (query.state.data?.state === 'ready' ? Infinity : 0),
    // SSE message.body_ready is the fast path; polling is the safety net so a
    // dropped event (proxy buffering, reconnect gap) can't strand the spinner.
    refetchInterval: (query) => (query.state.data?.state === 'pending' ? 2_500 : false),
  });
}

export function fetchMessageBody(
  messageId: number,
  images: RemoteImageRequestMode = 'blocked',
  signal?: AbortSignal,
) {
  const query = remoteImageQuery(images);
  return api(`/api/messages/${messageId}/body${query}`, {
    schema: MessageBody,
    signal,
  });
}

function remoteImageQuery(
  images: RemoteImageRequestMode,
  extra: readonly [string, string] | null = null,
): string {
  const params = new URLSearchParams();
  if (images === 'standard') params.set('images', '1');
  if (images === 'compatibility') params.set('images', 'compatibility');
  if (images === 'direct') params.set('images', 'direct');
  if (extra) params.set(extra[0], extra[1]);
  const value = params.toString();
  return value ? `?${value}` : '';
}

export function useRemoteImageSenderPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, allow }: { messageId: number; allow: boolean }) =>
      api(`/api/messages/${messageId}/remote-images/sender`, {
        method: allow ? 'PUT' : 'DELETE',
        schema: RemoteImageSenderPreference,
      }),
    onSuccess: () => mailInvalidations.senderImages.invalidate(qc),
  });
}

export function useMailAction() {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (body: MessageActionRequest) =>
      api('/api/messages/actions', {
        method: 'POST',
        body,
        schema: MessageActionResponse,
      }),
    onMutate: async (body) => {
      await Promise.all([
        qc.cancelQueries({ queryKey: mailKeys.lists }),
        qc.cancelQueries({ queryKey: mailKeys.searches }),
        qc.cancelQueries({ queryKey: mailKeys.threads }),
      ]);
      const snapshots: [QueryKey, unknown][] = [
        ...qc.getQueriesData({ queryKey: mailKeys.lists }),
        ...qc.getQueriesData({ queryKey: mailKeys.searches }),
        ...qc.getQueriesData({ queryKey: mailKeys.threads }),
      ];
      patchMailCaches(qc, body);
      return { snapshots };
    },
    onError: (_error, _body, context) => {
      for (const [key, data] of context?.snapshots ?? []) qc.setQueryData(key, data);
    },
    onSettled: () => {
      void mailInvalidations.actions.invalidate(qc);
    },
  });

  return {
    ...mutation,
    undo: async (undo: MessageActionUndo): Promise<void> => {
      await Promise.all([
        qc.cancelQueries({ queryKey: mailKeys.lists }),
        qc.cancelQueries({ queryKey: mailKeys.searches }),
        qc.cancelQueries({ queryKey: mailKeys.threads }),
      ]);
      const snapshots: [QueryKey, unknown][] = [
        ...qc.getQueriesData({ queryKey: mailKeys.lists }),
        ...qc.getQueriesData({ queryKey: mailKeys.searches }),
        ...qc.getQueriesData({ queryKey: mailKeys.threads }),
      ];
      for (const request of undo.requests) patchMailCaches(qc, request);
      try {
        for (const request of undo.requests) {
          await api('/api/messages/actions', {
            method: 'POST',
            body: request,
            schema: MessageActionResponse,
          });
        }
      } catch (error) {
        for (const [key, data] of snapshots) qc.setQueryData(key, data);
        throw error;
      } finally {
        void mailInvalidations.actions.invalidate(qc);
      }
    },
  };
}

/** Resolve a bounded full-search selection on the server immediately before acting. */
export function useSearchMailAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SearchMessageActionRequest) =>
      api('/api/search/actions', {
        method: 'POST',
        body,
        schema: SearchMessageActionResponse,
      }),
    onSettled: () => mailInvalidations.actions.invalidate(qc),
  });
}

export function useEmptyTrash() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      folderId,
      onProgress,
    }: {
      folderId: number;
      onProgress?: (progress: EmptyTrashProgress) => void;
    }): Promise<EmptyTrashResult> =>
      drainEmptyTrashBatches(
        () =>
          api(`/api/folders/${folderId}/empty-trash`, {
            method: 'POST',
            schema: EmptyTrashBatchResponse,
          }),
        onProgress,
      ),
    onSettled: () => mailInvalidations.actionsAndSync.invalidate(qc),
  });
}

function patchMailCaches(qc: QueryClient, request: MessageActionRequest): void {
  const selectedThreads = new Set(request.threadIds ?? []);
  const selectedMessages = new Set(request.messageIds ?? []);

  if (selectedMessages.size > 0) {
    for (const [, data] of qc.getQueriesData({ queryKey: mailKeys.threads })) {
      if (!isThreadDetail(data)) continue;
      if (data.messages.some((message) => selectedMessages.has(message.id))) {
        selectedThreads.add(data.id);
      }
    }
  }

  qc.setQueriesData({ queryKey: mailKeys.threads }, (data) =>
    isThreadDetail(data) ? patchThreadDetail(data, request, selectedMessages) : data,
  );
  qc.setQueriesData({ queryKey: mailKeys.lists }, (data) =>
    patchListData(data, selectedThreads, request.action, true),
  );
  qc.setQueriesData({ queryKey: mailKeys.searches }, (data) =>
    patchListData(data, selectedThreads, request.action, false),
  );
}

function patchThreadDetail(
  detail: ThreadDetailType,
  request: MessageActionRequest,
  selectedMessages: Set<number>,
): ThreadDetailType {
  const wholeThread = request.threadIds?.includes(detail.id) ?? false;
  if (!wholeThread && !detail.messages.some((message) => selectedMessages.has(message.id))) {
    return detail;
  }
  if (request.action === 'delete_perm') {
    return {
      ...detail,
      messages: detail.messages.filter((message) =>
        wholeThread
          ? message.folderId !== request.sourceFolderId
          : !selectedMessages.has(message.id),
      ),
    };
  }
  if (!['read', 'unread', 'star', 'unstar'].includes(request.action)) return detail;

  return {
    ...detail,
    messages: detail.messages.map((message) => {
      if (!wholeThread && !selectedMessages.has(message.id)) return message;
      switch (request.action) {
        case 'read':
          return { ...message, isRead: true };
        case 'unread':
          return { ...message, isRead: false };
        case 'star':
          return { ...message, isStarred: true };
        case 'unstar':
          return { ...message, isStarred: false };
        default:
          return message;
      }
    }),
  };
}

function patchListData(
  data: unknown,
  selectedThreads: Set<number>,
  action: MessageActionRequest['action'],
  removeMoving: boolean,
): unknown {
  if (isThreadListResponse(data)) {
    return patchThreadListPage(data, selectedThreads, action, removeMoving);
  }
  if (!data || typeof data !== 'object' || !('pages' in data) || !Array.isArray(data.pages)) {
    return data;
  }
  return {
    ...data,
    pages: data.pages.map((page) =>
      isThreadListResponse(page)
        ? patchThreadListPage(page, selectedThreads, action, removeMoving)
        : page,
    ),
  };
}

function patchThreadListPage(
  page: ThreadListResponseType,
  selectedThreads: Set<number>,
  action: MessageActionRequest['action'],
  removeMoving: boolean,
): ThreadListResponseType {
  const moving =
    action === 'delete_perm' ||
    (removeMoving &&
      (
        action === 'archive' ||
        action === 'trash' ||
        action === 'spam' ||
        action === 'not_spam' ||
        action === 'move'
      ));
  return {
    ...page,
    rows: moving
      ? page.rows.filter((row) => !selectedThreads.has(row.id))
      : page.rows.map((row) =>
          selectedThreads.has(row.id) ? patchThreadRow(row, action) : row,
        ),
  };
}

function patchThreadRow(
  row: ThreadRow,
  action: MessageActionRequest['action'],
): ThreadRow {
  switch (action) {
    case 'read':
      return { ...row, unread: false };
    case 'unread':
      return { ...row, unread: true };
    case 'star':
      return { ...row, starred: true };
    case 'unstar':
      return { ...row, starred: false };
    default:
      return row;
  }
}

function isThreadDetail(data: unknown): data is ThreadDetailType {
  return (
    data !== null &&
    typeof data === 'object' &&
    'id' in data &&
    typeof data.id === 'number' &&
    'messages' in data &&
    Array.isArray(data.messages)
  );
}

function isThreadListResponse(data: unknown): data is ThreadListResponseType {
  return (
    data !== null &&
    typeof data === 'object' &&
    'rows' in data &&
    Array.isArray(data.rows) &&
    'nextCursor' in data
  );
}
